/** Canonical auth modal paths (query params carry email / verifyCode). */
export const AUTH_PATHS = {
  SIGN_IN: '/signIn',
  SIGN_UP_VERIFY: '/signUpVerify',
} as const;

const AUTH_PATH_SET = new Set<string>(Object.values(AUTH_PATHS));

export function buildSignUpVerifyUrl(email: string, verifyCode?: string): string {
  const params = new URLSearchParams();
  params.set('email', email);
  if (verifyCode?.trim()) {
    params.set('verifyCode', verifyCode.trim());
  }
  return `${AUTH_PATHS.SIGN_UP_VERIFY}?${params.toString()}`;
}

export function buildSignInUrl(email?: string): string {
  const params = new URLSearchParams();
  if (email?.trim()) {
    params.set('email', email.trim());
  }
  const qs = params.toString();
  return qs ? `${AUTH_PATHS.SIGN_IN}?${qs}` : AUTH_PATHS.SIGN_IN;
}

export function isAuthModalPath(pathname: string): boolean {
  return AUTH_PATH_SET.has(pathname);
}

/** OAuth must not return to auth modal routes — they reopen the sign-in UI after success. */
export function resolveOAuthReturnPath(pathname: string, search = '', hash = ''): string {
  if (isAuthModalPath(pathname)) {
    return '/';
  }
  return `${pathname}${search}${hash}` || '/';
}

export function resolveOAuthReturnPathFromUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl, window.location.origin);
    return resolveOAuthReturnPath(url.pathname, url.search, url.hash);
  } catch {
    const pathOnly = rawUrl.split('?')[0].split('#')[0];
    if (pathOnly.startsWith('/') && isAuthModalPath(pathOnly)) {
      return '/';
    }
    return rawUrl.startsWith('/') ? rawUrl : '/';
  }
}

/** Persist post-OAuth navigation target; strips auth modal paths (/signIn, /signUpVerify). */
export function captureOAuthReturnUrl(): void {
  const { pathname, search, hash } = window.location;
  const path = resolveOAuthReturnPath(pathname, search, hash);
  sessionStorage.setItem('auth_return_url', `${window.location.origin}${path}`);
}

/** Remove auth modal URL (dedicated path → home; legacy query params stripped in place). */
export function clearAuthModalUrl(): void {
  const { pathname, search, hash } = window.location;

  if (isAuthModalPath(pathname)) {
    window.history.replaceState({}, '', '/');
    return;
  }

  const params = new URLSearchParams(search);
  const keys = ['signin', 'purpose', 'email', 'code', 'verifyCode'];
  let changed = false;
  keys.forEach(key => {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  });

  if (changed) {
    const qs = params.toString();
    window.history.replaceState({}, '', `${pathname}${qs ? `?${qs}` : ''}${hash}`);
  }
}

export function parseAuthEmail(raw: unknown): string {
  if (raw == null || raw === 'undefined') return '';
  return decodeURIComponent(String(raw));
}

export function parseVerifyCode(raw: unknown, legacyCode?: unknown): string {
  const value = raw ?? legacyCode;
  if (value == null || value === 'undefined') return '';
  return String(value).trim();
}
